import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { FormControl, Grid, TextField, Typography } from "@material-ui/core";
import { PaymentTerminalBadge } from "components/PaymentTerminalBadge/PaymentTerminalBadge";
import Box from "@mui/material/Box";
import { Autocomplete } from "@material-ui/lab";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

import { Checkbox, FormControlLabel } from "@material-ui/core";
import { animations } from "helpers/animations";
import { cleanNumber } from "helpers/cleanNumbers";
import { orderArrayV2 } from "helpers/orderArray";
import { orderOriginOptions, PAYMENT_METHODS } from "helpers/dictionary";
import { combineSuggestions } from "helpers/suggestionUtils";
import { SelectComponent } from "components/SelectMenu/SelectComponent";
import RegularButton from "components/CustomButtons/Button";
import { SwitchCustom } from "components/SwitchCustom/SwitchCustom";
import { PERMISSIONS } from "router/permissions";

import {
  DisplayAccountCredit,
  ShowCreditLimit,
  DisplayRequestCredit,
  ShowCreditSuspend,
} from "../accounts/DisplayAccountCredit";

import { useOrderForm } from "./hooks/useOrderForm";
import { useSuggestions } from "./hooks/useSuggestions";
import { useSentiment } from "./hooks/useSentiment";
import { AccountInfoCard } from "./components/AccountInfoCard";
import { SuggestionAlert } from "./components/SuggestionAlert";
import { SuggestionFeedbackModal } from "./SuggestionFeedbackModal";
import "./SuggestedDateStyles.css";

export const CustomerOrderFormModal = ({
  account,
  onClose,
  showAccountSelector = false,
}) => {
  const { CUSTOMER_SERVICES } = PERMISSIONS;
  const { allAccounts } = useSelector(
    ({ customersReducer }) => customersReducer,
  );
  const { suggestedUnits } = useSelector(({ unitsReducer }) => unitsReducer);

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [pendingFormSubmission, setPendingFormSubmission] = useState(null);
  const [lastValidatedCoupon, setLastValidatedCoupon] = useState('');
  const [fullTank, setFullTank] = useState(false);

  // --- Order form hook ---
  const orderForm = useOrderForm({ account, showAccountSelector, onClose });
  const {
    inputValues,
    handleInputOnChange,
    handleNewValues,
    cleanForm,
    setFieldErrors,
    fieldErrors,
    runValidates,
    checkboxOnChange,
    selectedUnit,
    setSelectedUnit,
    selectedBrand,
    setSelectedBrand,
    brandOptions,
    brandLocked,
    setBrandLocked,
    unitsOpt,
    setUnitsOpt,
    dates,
    setDates,
    validatedSchedules,
    notFunds,
    validateType,
    creditAccount,
    disabledCredit,
    limitDays,
    customer,
    products,
    stationUnits,
    isLoadingComp,
    requestCredit,
    myCallID,
    paymentMethods,
    appliedCoupon,
    handleDates,
    handleByLts,
    handleByPrice,
    handleObservations,
    handleAccountSelect,
    id,
    alias,
    idStation,
    type,
    address,
    idCustomer,
    dispatch,
    newCustomerService,
    checkCoupon,
  } = orderForm;

  const { date, schedule, product, quantity, price, observations, origin, coupon, discount, paymentMethod } =
    inputValues;

  // --- Suggestions hook ---
  const suggestions = useSuggestions({
    account,
    inputValues,
    products,
    selectedBrand,
    stationUnits,
    unitsOpt,
    setUnitsOpt,
    validatedSchedules,
    handleNewValues,
    setSelectedUnit,
    selectedUnit,
  });
  const {
    suggestionStatus,
    suggestedUnitInfo,
    allSuggestions,
    suggestedDate,
    suggestedSchedule,
    hasSuggestedSchedule,
    isSuggestedUnit,
    retryCount,
    handleRetry,
    handleUnitChange,
    resetSuggestionState,
    clearUnitSuggestions,
    stationConfig,
  } = suggestions;

  // --- Sentiment hook ---
  const sentiment = useSentiment({
    account,
    products,
    inputValues,
    handleNewValues,
  });
  const {
    sentimentData,
    isSuggestedQuantity,
    isMatchingProduct,
    isMatchingSuggestedQuantity,
    resetSentiment,
  } = sentiment;

  // --- Coupon logic ---
  const validateCoupon = (e) => {
    const couponValue = e.target.value?.trim();
    if (!couponValue || couponValue === lastValidatedCoupon) return;
    setLastValidatedCoupon(couponValue);
    dispatch(checkCoupon({
      code: couponValue,
      idCustomer,
      idAccount: id,
      idProduct: product,
      idStation,
      idBrand: selectedBrand?.idBrand
    }));
  };

  useEffect(() => {
    if (!coupon) {
      handleNewValues({ discount: 0 });
      setFieldErrors({ price: '' });
      setLastValidatedCoupon('');
      return;
    }

    if (appliedCoupon?.coupon && appliedCoupon?.rule) {
      const priceNum = cleanNumber(price);
      const currentProduct = products.find((prod) => prod.id === product);

      if (priceNum < +appliedCoupon.rule.minimum) {
        handleNewValues({ discount: 0 });
        setFieldErrors({ price: 'Monto mínimo del cupón: $' + appliedCoupon.rule.minimum });
        return;
      }

      setFieldErrors({ price: '' });
      let appliedDiscount = 0;

      if (appliedCoupon.rule.unit === 'PORCENTAJE') {
        appliedDiscount = (priceNum * +appliedCoupon.rule.discount) / 100;
      } else if (appliedCoupon.rule.unit === 'IMPORTE') {
        appliedDiscount = +appliedCoupon.rule.discount;
      } else if ((appliedCoupon.rule.unit === 'LTS' || appliedCoupon.rule.unit === 'KGS') && currentProduct) {
        const discountQuantity = +appliedCoupon.rule.discount;
        const pricePerUnit = currentProduct.price * currentProduct.capacity;
        appliedDiscount = discountQuantity * pricePerUnit;
      }

      const safeDiscount = isNaN(appliedDiscount) ? 0 : +appliedDiscount.toFixed(2);
      handleNewValues({ discount: safeDiscount });
    } else if (coupon && lastValidatedCoupon === coupon) {
      handleNewValues({ discount: 0 });
      setFieldErrors({ price: '' });
    }
  }, [appliedCoupon, price, product, coupon, quantity, products]);

  // "Tanque lleno" solo aplica a productos que no son portátiles
  const allowFullTank = !!product && products.find((prod) => prod.id === product)?.type !== "PORTATIL";

  useEffect(() => {
    if (!allowFullTank) setFullTank(false);
  }, [allowFullTank]);

  // --- Handlers ---
  const handleChange = (e, value) => {
    setSelectedUnit(value);
    handleNewValues({ ...inputValues, idUnit: value?.id });
    handleUnitChange(value);
  };

  const handleSubmit = () => {
    const error = runValidates(fullTank ? ['quantity', 'price'] : []);
    if (error) return;
    const { price, ...newValues } = inputValues;

    const combined = combineSuggestions(allSuggestions);

    const form = {
      ...newValues,
      idSchedule: dates.find((obj) => obj.value === newValues.schedule)?.id,
      idAccount: id,
      idStation,
      callID: myCallID,
      origin,
      paymentMethod: paymentMethod || PAYMENT_METHODS.CASH,
      paymentType: paymentMethod || PAYMENT_METHODS.CASH,
      idCoupon: fullTank ? undefined : appliedCoupon?.coupon?.id,
      idBrand:
        selectedBrand && selectedBrand?.name !== "Todos"
          ? selectedBrand.idBrand
          : stationUnits.find((obj) => obj.brand)?.idBrand,
      details: [{ id: product, quantity: fullTank ? 0 : cleanNumber(quantity) }],
      metadata: {
        rapidCanvas: { ...combined, attempts: allSuggestions },
      },
    };

    const hasSuggs = allSuggestions.length > 0;
    const hasValidUnitSuggs = allSuggestions.some(
      (s) => s.camion && Object.values(s.camion).some((v) => v !== null),
    );

    if (hasSuggs && hasValidUnitSuggs && !selectedUnit?.suggested) {
      setPendingFormSubmission(form);
      setShowFeedbackModal(true);
    } else {
      dispatch(newCustomerService(form, handleClose));
      cleanForm();
    }
  };

  const handleFeedbackSubmit = (reason) => {
    if (pendingFormSubmission) {
      const formWithFeedback = {
        ...pendingFormSubmission,
        metadata: {
          ...pendingFormSubmission.metadata,
          rapidCanvas: {
            ...pendingFormSubmission.metadata.rapidCanvas,
            feedbackReason: reason,
          },
        },
      };
      dispatch(newCustomerService(formWithFeedback, handleClose));
      cleanForm();
    }
  };

  const handleClose = () => {
    setSelectedUnit(null);
    setSelectedBrand(null);
    setBrandLocked(false);
    setFullTank(false);
    resetSentiment();
    setUnitsOpt((prev) =>
      prev.map((unit) => {
        const { suggested, ...clean } = unit;
        return clean;
      }),
    );
    resetSuggestionState();
    setFieldErrors({});
    cleanForm();
    onClose();
  };

  const showSentimentDot =
    isSuggestedQuantity && isMatchingProduct && isMatchingSuggestedQuantity;

  return (
    <FormControl className={animations.fadeIn}>
      <Grid container spacing={1}>
        {/* Account selector */}
        {showAccountSelector && (
          <Grid item xs={12}>
            <Autocomplete
              id="account-autocomplete"
              options={customer?.accounts || []}
              value={account}
              getOptionLabel={(acc) => acc.alias}
              getOptionSelected={(option, value) => option?.id === value?.id}
              renderOption={(option) => {
                const addressText =
                  typeof option.address === "object"
                    ? `${option.address?.street || ""} ${option.address?.extNumber || ""}, ${option.address?.colony || ""}`
                    : option.address || "Sin dirección";
                return (
                  <Box title={addressText} style={{ cursor: "pointer" }}>
                    <Typography variant="body1">{option.alias}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {addressText}
                    </Typography>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label="Cuenta *" variant="outlined" />
              )}
              fullWidth
              onChange={(e, value) => handleAccountSelect(value)}
            />
          </Grid>
        )}

        {/* Account info card */}
        {account && (
          <AccountInfoCard account={account} sentimentData={sentimentData} />
        )}

        {/* Credit info */}
        {creditAccount && (
          <Grid item xs={12}>
            <DisplayAccountCredit creditAccount={creditAccount} />
          </Grid>
        )}
        {requestCredit && (
          <Grid item xs={12}>
            <DisplayRequestCredit />
          </Grid>
        )}

        {/* Brand */}
        <Grid item xs={12}>
          <Autocomplete
            id="brand-autocomplete"
            options={brandOptions}
            value={selectedBrand}
            getOptionLabel={(brand) => brand.name || brand.alias}
            getOptionSelected={(option, value) =>
              option.idBrand === value.idBrand
            }
            renderInput={(params) => (
              <TextField {...params} label="Marca" variant="outlined" />
            )}
            fullWidth
            onChange={(e, value) => {
              setSelectedBrand(value);
              setSelectedUnit(null);
              handleNewValues({ ...inputValues, idUnit: null });
              resetSuggestionState();
              clearUnitSuggestions();
            }}
            disabled={brandLocked}
          />
        </Grid>

        {/* Product */}
        <Grid item xs={12}>
          <SelectComponent
            label="Producto *"
            name="product"
            value={product}
            onChange={handleInputOnChange}
            options={products}
            error={fieldErrors.product}
            noData="No hay precios para la zona"
          />
        </Grid>

        {/* Full tank */}
        {allowFullTank && (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={fullTank}
                onChange={(e) => {
                  setFullTank(e.target.checked);
                  // Con tanque lleno el total no se conoce, por lo que no se permite cupón
                  if (e.target.checked) handleNewValues({ quantity: '', price: '', coupon: '', discount: 0 });
                }}
                color="primary"
              />
            }
            label="Tanque lleno"
          />
        </Grid>
        )}

        {/* Quantity & Price */}
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Cantidad"
            name="quantity"
            InputProps={{
              startAdornment: showSentimentDot && (
                <Box width={8} height={8} bgcolor="#00e676" borderRadius="50%" mr={1} />
              ),
              endAdornment: (
                <Typography style={{ marginLeft: 4 }}>
                  {products[0]?.measureUnit}
                </Typography>
              ),
              inputProps: { min: 0, right: "1" },
            }}
            value={quantity}
            onChange={handleByLts}
            variant="outlined"
            disabled={fullTank}
            error={fieldErrors.quantity}
            helperText={fieldErrors.quantity || ""}
          />
          {isSuggestedQuantity && (
            <Box
              mt={1}
              px={2}
              py={1}
              borderRadius={1}
              style={{ backgroundColor: "#E8F5E9", color: "#2e7d32" }}
            >
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                Sugerencia: {sentimentData?.product_name} -{" "}
                {sentimentData?.quantity}
              </Typography>
            </Box>
          )}
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Importe"
            name="price"
            InputProps={{
              startAdornment: (
                <>
                  {showSentimentDot && (
                    <Box width={8} height={8} bgcolor="#00e676" borderRadius="50%" mr={1} />
                  )}
                  <Typography style={{ marginLeft: 4 }}>$ </Typography>
                </>
              ),
              inputProps: { min: 0, right: "1" },
            }}
            value={price}
            onChange={handleByPrice}
            variant="outlined"
            error={fieldErrors.price}
            helperText={fieldErrors.price || ""}
            disabled={validateType || fullTank}
          />
        </Grid>

        {/* Date & Schedule */}
        <Grid item xs={4}>
          <DatePicker
            selected={date ? new Date(date + "T00:00:00") : null}
            onChange={(newDate) => {
              if (newDate) {
                const year = newDate.getFullYear();
                const month = String(newDate.getMonth() + 1).padStart(2, "0");
                const day = String(newDate.getDate()).padStart(2, "0");
                handleDates(
                  { target: { value: `${year}-${month}-${day}` } },
                  stationConfig[CUSTOMER_SERVICES],
                );
              }
            }}
            dateFormat="dd/MM/yyyy"
            minDate={limitDays.min ? new Date(limitDays.min) : undefined}
            maxDate={limitDays.max ? new Date(limitDays.max) : undefined}
            dayClassName={(d) => {
              const dateString = d.toISOString().split("T")[0];
              const hasValid =
                suggestedUnits.camion &&
                Object.values(suggestedUnits.camion).some((v) => v !== null);
              let isSuggested =
                hasValid &&
                suggestedUnits.fecha &&
                Object.values(suggestedUnits.fecha).includes(dateString);
              if (!isSuggested) {
                isSuggested = allSuggestions.some((s) =>
                  Object.values(s.fecha).includes(dateString),
                );
              }
              return isSuggested ? "suggested-date" : "";
            }}
            popperProps={{ style: { zIndex: 9999 } }}
            customInput={
              <TextField
                label="Fecha de Surtido"
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: suggestedDate && date && date === suggestedDate && (
                    <Box width={8} height={8} bgcolor="#00e676" borderRadius="50%" mr={1} />
                  ),
                  endAdornment: (
                    <Box style={{ cursor: "pointer", color: "#666" }}>📅</Box>
                  ),
                }}
                style={{ width: "100%" }}
              />
            }
          />
        </Grid>
        <Grid item xs={4}>
          <Box position="relative">
            <SelectComponent
              disabled={
                suggestedUnits &&
                stationConfig[CUSTOMER_SERVICES]?.forceSchedule &&
                hasSuggestedSchedule
              }
              label="Horario *"
              name="schedule"
              value={schedule}
              onChange={handleInputOnChange}
              options={dates.map((dateOption) => {
                let isSuggested = false;
                if (suggestedSchedule && dateOption.value) {
                  const suggestedHour = parseInt(suggestedSchedule.split(":")[0]);
                  const [startTime, endTime] = dateOption.value.split(" - ");
                  const startHour = parseInt(startTime?.split(":")[0]);
                  const endHour = parseInt(endTime?.split(":")[0]);
                  isSuggested = suggestedHour === startHour || suggestedHour === endHour;
                }
                return { ...dateOption, suggested: isSuggested };
              })}
              error={!!fieldErrors.schedule}
            />
          </Box>
        </Grid>
        <Grid item xs={4} style={{ display: "flex", alignItems: "center" }}>
          <SwitchCustom
            name="isAsap"
            label="Lo antes Posible"
            checked={!!inputValues.isAsap}
            onChange={checkboxOnChange}
          />
        </Grid>

        {/* Unit & ETA */}
        <Grid item xs={suggestedUnitInfo?.etaMax ? 9 : 12}>
          <Autocomplete
            disabled={suggestedUnits && stationConfig[CUSTOMER_SERVICES]?.forceUnit}
            id="unit-autocomplete"
            options={orderArrayV2(unitsOpt)}
            value={selectedUnit}
            getOptionLabel={(unit) => unit.label}
            renderOption={(option) => (
              <Box display="flex" alignItems="center" gap={1}>
                {option.suggested && (
                  <Box width={8} height={8} bgcolor="#00e676" borderRadius="50%" />
                )}
                <span>{option.label}</span>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Unidad"
                variant="outlined"
                error={!!fieldErrors.idUnit}
                helperText={fieldErrors.idUnit}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: selectedUnit?.suggested && (
                    <Box width={8} height={8} bgcolor="#00e676" borderRadius="50%" mr={1} />
                  ),
                  endAdornment: params.InputProps.endAdornment,
                }}
              />
            )}
            fullWidth
            onChange={handleChange}
          />
        </Grid>
        {suggestedUnitInfo?.etaMax && (
          <Grid item xs={3}>
            <TextField
              label="ETA"
              value={String(suggestedUnitInfo.etaMax)}
              variant="outlined"
              fullWidth
              disabled
              InputProps={{ style: { fontWeight: 500 } }}
            />
          </Grid>
        )}
        {selectedUnit && (
          <PaymentTerminalBadge paymentTerminal={selectedUnit.hasPaymentTerminal} />
        )}

        {/* Origin */}
        <Grid item xs={12}>
          <SelectComponent
            label="Origen"
            name="origin"
            value={origin}
            onChange={handleInputOnChange}
            options={orderOriginOptions}
          />
        </Grid>

        {/* Suggestion alert */}
        <SuggestionAlert
          suggestionStatus={suggestionStatus}
          retryCount={retryCount}
          unitsOpt={unitsOpt}
          selectedBrand={selectedBrand}
          onRetry={handleRetry}
        />

        {/* Coupon */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Cupón"
            name="coupon"
            value={coupon}
            onChange={handleInputOnChange}
            onBlur={validateCoupon}
            variant="outlined"
            disabled={fullTank}
            error={fieldErrors.coupon}
            helperText={fullTank ? "No aplica con tanque lleno" : fieldErrors.coupon || ""}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Descuento"
            value={discount}
            variant="outlined"
            InputProps={{
              startAdornment: discount > 0 && (
                <Typography style={{ marginLeft: 4 }}>$ </Typography>
              ),
              inputProps: { min: 0, right: "1" },
            }}
            disabled
          />
        </Grid>

        {/* Payment method */}
        <Grid item xs={12}>
          <SelectComponent
            label="Método de pago *"
            name="paymentMethod"
            value={paymentMethod}
            onChange={handleInputOnChange}
            options={paymentMethods}
            error={fieldErrors.paymentMethod}
            noData="No hay métodos de pago"
          />
        </Grid>

        {/* Observations */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Observaciones"
            name="observations"
            value={observations}
            onChange={handleInputOnChange}
            variant="outlined"
            multiline
            onClick={handleObservations}
          />
        </Grid>

        {/* Credit warnings */}
        {notFunds && <ShowCreditLimit />}
        {(account?.isCreditSuspended ||
          (creditAccount?.suspendedDate &&
            moment(creditAccount?.suspendedDate).isSameOrBefore(moment(), "day"))) &&
          !requestCredit && (
            <ShowCreditSuspend date={creditAccount?.suspendedDate} />
          )}

        {/* Actions */}
        <Grid
          item
          xs={12}
          style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
        >
          <RegularButton
            color="danger"
            onClick={handleClose}
            disabled={isLoadingComp?.loadingCreateOrder}
          >
            Cancelar
          </RegularButton>
          <RegularButton
            color="info"
            onClick={handleSubmit}
            disabled={
              isLoadingComp?.loadingCreateOrder ||
              products?.length < 1 ||
              disabledCredit
            }
          >
            Crear pedido
          </RegularButton>
        </Grid>
      </Grid>

      <SuggestionFeedbackModal
        open={showFeedbackModal}
        onClose={() => {
          setShowFeedbackModal(false);
          setPendingFormSubmission(null);
        }}
        onSubmit={handleFeedbackSubmit}
      />
    </FormControl>
  );
};
